import React from 'react';
import { useTranslation } from 'react-i18next';

import { General } from 'icons/untitledui';
import Switch from 'shared/_styles/Switch.style';
import * as Buttons from 'theme/components/Buttons.style';
import * as Text from 'theme/components/Text.style';
import * as Section from 'settings/styles/SettingsSection.style';
import type { PushStatus } from 'pwa/helpers/pushNotifications';
import type { NotificationChannel } from 'shared/hooks/api/notificationSettings';
import PillSelect from 'settings/components/PillSelect';
import getPushNoticeKey, { PushNoticeKey } from 'settings/utils/pushNotice';
import ShiftSchedule, {
  ShiftScheduleSettings,
} from 'settings/components/ShiftSchedule';

export interface NotificationTypeSetting {
  enabled: boolean;
  channel: NotificationChannel;
}

export interface NotificationTypeSettings {
  newEvents: NotificationTypeSetting;
  shiftReminders: NotificationTypeSetting;
  shiftReports: NotificationTypeSetting;
}

export type NotificationTypeKey = keyof NotificationTypeSettings;

export interface DevicePush {
  status: PushStatus | null;
  isBusy: boolean;
  hasFailed: boolean;
  onEnable: () => void;
}

interface NotificationCardsProps {
  value: NotificationTypeSettings;
  onChange: (next: NotificationTypeSettings) => void;
  schedule: ShiftScheduleSettings;
  onScheduleChange: (next: ShiftScheduleSettings) => void;
  device: DevicePush;
  types?: NotificationTypeKey[];
}

export const NOTIFICATION_TYPE_KEYS: NotificationTypeKey[] = [
  'newEvents',
  'shiftReports',
  'shiftReminders',
];

const descriptionId = (key: NotificationTypeKey) =>
  `notification-${key}-description`;

const usesPush = (setting: NotificationTypeSetting) =>
  setting.enabled && setting.channel !== 'email';

export default function NotificationCards({
  value,
  onChange,
  schedule,
  onScheduleChange,
  device,
  types = NOTIFICATION_TYPE_KEYS,
}: NotificationCardsProps): JSX.Element {
  const { t } = useTranslation('pageSettings');

  const channelOptions = [
    { value: 'push', label: t('notifications.channel.push') },
    { value: 'email', label: t('notifications.channel.email') },
    { value: 'both', label: t('notifications.channel.both') },
  ];

  const update = (
    key: NotificationTypeKey,
    patch: Partial<NotificationTypeSetting>
  ) => {
    const next = { ...value, [key]: { ...value[key], ...patch } };
    onChange(next);
    if (usesPush(next[key]) && device.status === 'off' && !device.isBusy) {
      device.onEnable();
    }
  };

  const wantsPush = types.some((key) => usesPush(value[key]));
  const pushNoticeKey = getPushNoticeKey(
    wantsPush,
    device.status,
    device.hasFailed
  );
  const pushNoticeActions: Partial<Record<PushNoticeKey, string>> = {
    failed: t('notifications.pushNotice.tryAgain'),
    notAllowed: t('notifications.pushNotice.allow'),
  };

  return (
    <>
      <Section.SectionHeader>
        <Text.H5>{t('notifications.title')}</Text.H5>
        <Text.Label>
          {t(
            wantsPush ? 'notifications.helperWithPush' : 'notifications.helper'
          )}
        </Text.Label>
      </Section.SectionHeader>

      <Section.NotificationsBody>
        <div role="status">
          {pushNoticeKey && (
            <Section.PushNoticeCard>
              <Section.PushNoticeMessage>
                <Section.PushNoticeIcon aria-hidden>
                  <General.Bell04 size={20} />
                </Section.PushNoticeIcon>
                <Text.P2Semibold>
                  {t(`notifications.pushNotice.${pushNoticeKey}`)}
                </Text.P2Semibold>
              </Section.PushNoticeMessage>
              {pushNoticeActions[pushNoticeKey] && (
                <Buttons.SecondaryDark
                  $size="small"
                  inactive={device.isBusy}
                  disabled={device.isBusy}
                  onClick={device.onEnable}
                >
                  {pushNoticeActions[pushNoticeKey]}
                </Buttons.SecondaryDark>
              )}
            </Section.PushNoticeCard>
          )}
        </div>

        <Section.CardStack>
          {types.map((key) => {
            const setting = value[key];
            const title = t(`notifications.types.${key}.title`);
            return (
              <Section.SectionCard key={key}>
                <Section.Rows>
                  <Section.RowListItem>
                    <Section.RowLabel>
                      <Text.P2Semibold>{title}</Text.P2Semibold>
                      <Section.RowDescription id={descriptionId(key)}>
                        {t(`notifications.types.${key}.description`)}
                      </Section.RowDescription>
                    </Section.RowLabel>
                    <Section.RowControl>
                      <Switch
                        checked={setting.enabled}
                        inputProps={{
                          'aria-label': title,
                          'aria-describedby': descriptionId(key),
                        }}
                        onChange={(event) =>
                          update(key, { enabled: event.target.checked })
                        }
                      />
                    </Section.RowControl>
                  </Section.RowListItem>

                  {setting.enabled && (
                    <>
                      <Section.Divider />
                      <Section.RowListItem $mobileColumn>
                        <Section.RowLabel>
                          <Text.P2Semibold>
                            {t('notifications.notifyMeVia')}
                          </Text.P2Semibold>
                        </Section.RowLabel>
                        <Section.RowControl>
                          <PillSelect
                            ariaLabel={`${title}: ${t(
                              'notifications.notifyMeVia'
                            )}`}
                            value={setting.channel}
                            options={channelOptions}
                            onChange={(next) =>
                              update(key, {
                                channel: next as NotificationChannel,
                              })
                            }
                          />
                        </Section.RowControl>
                      </Section.RowListItem>
                    </>
                  )}

                  {key === 'shiftReminders' && setting.enabled && (
                    <>
                      <Section.Divider />
                      <ShiftSchedule
                        value={schedule}
                        onChange={onScheduleChange}
                      />
                    </>
                  )}
                </Section.Rows>
              </Section.SectionCard>
            );
          })}
        </Section.CardStack>
      </Section.NotificationsBody>
    </>
  );
}
